import React, { useState } from 'react';
import { ProductsManagement, CategoryManagement, CustomersManagement, ShippingSettings } from './AdminComponents';
import { OrderNumberManagement, MarketingManagement, EmailTemplatesManagement, AnalyticsDashboard, CreatedOrdersManagement } from './AdminExtensions';
import { WebsiteCustomizationManagement } from './WebsiteCustomization';
import { EnhancedOrdersManagement } from './AdminExtensions';
import { InventoryManagementEnhanced } from './InventoryManagementEnhanced';
import { FinancialReports } from './FinancialReports';
import { DiscountCodesManagement } from './DiscountCodesManagement';
import { SEOManagement } from './SEOManagement';
import { ContentManagement } from './ContentManagement';
import AIProductsOrders from './AIProductsOrders';
import { HomepageThemeSettings } from './HomepageThemeSettings';
import { BusinessInfoSettings } from './BusinessInfoSettings';
import { AdminUserManagement } from './AdminUserManagement';
import { SystemSettings } from './SystemSettings';
import { ContactFormManagement } from './ContactFormManagement';
import { PolicyManagement } from './PolicyManagement';
import { DesignerRequestsManager } from './AIProductsManagerExtensions2';
import { useThemeClasses } from '../hooks/useThemeClasses';

// ============================================
// NOTE: All feature components are now imported from separate files
// InventoryManagement, FinancialReports, DiscountCodesManagement,
// SEOManagement, ContentManagement, BusinessInfoSettings, 
// AdminUserManagement, and SystemSettings are all implemented
// ============================================

// Products & Catalog Section
export const ProductsCatalogSection = () => {
  const theme = useThemeClasses();
  const [activeSubTab, setActiveSubTab] = useState(() => {
    // Check if we should switch to products tab
    const shouldSwitch = sessionStorage.getItem('switchToProductsTab');
    if (shouldSwitch) {
      sessionStorage.removeItem('switchToProductsTab');
      return 'products';
    }
    return 'products';
  });

  return (
    <div className="space-y-6">
      <div className={`border-b ${theme.border}`}>
        <nav className="flex space-x-8">
          {[
            { id: 'products', label: '📦 Products', icon: '' },
            { id: 'categories', label: '📂 Categories', icon: '' },
            { id: 'inventory', label: '📊 Inventory', icon: '' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveSubTab(tab.id)}
              className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeSubTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : `border-transparent ${theme.textSecondary} hover:text-blue-600`
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {activeSubTab === 'products' && <ProductsManagement />}
      {activeSubTab === 'categories' && <CategoryManagement />}
      {activeSubTab === 'inventory' && <InventoryManagementEnhanced />}
    </div>
  );
};

// Create Order Tab Component
const CreateOrderTab = () => {
  const theme = useThemeClasses();
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productFields, setProductFields] = useState({});
  const [taxRate, setTaxRate] = useState(0.08); // Default 8%, will be loaded from settings
  
  // Customer info
  const [customerInfo, setCustomerInfo] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: ''
  });
  
  // Order details
  const [orderDetails, setOrderDetails] = useState({
    quantity: 1,
    customization_notes: '',
    shipping_address: {
      street: '',
      city: '',
      state: '',
      postal_code: '',
      country: 'US'
    },
    payment_method: 'bank_transfer',
    shipping_method: 'standard',
    deposit_amount: 0,
    include_tax: true
  });

  React.useEffect(() => {
    loadProducts();
    loadCustomers();
    loadBusinessSettings();
  }, []);

  // Force re-render when pricing-related fields change to update totals
  React.useEffect(() => {
    // This will trigger a re-render when productFields or orderDetails change
    // ensuring pricing calculations are updated dynamically
  }, [productFields, orderDetails.quantity, orderDetails.include_tax, orderDetails.shipping_method]);

  const loadProducts = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setProducts(data);
    } catch (error) {
      console.error('Failed to load products:', error);
    }
  };

  const loadCustomers = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/customers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setCustomers(data);
    } catch (error) {
      console.error('Failed to load customers:', error);
    }
  };

  const loadBusinessSettings = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/business-info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.tax_rate !== undefined && data.tax_rate !== null) {
        setTaxRate(data.tax_rate / 100); // Convert from percentage (13) to decimal (0.13)
      }
    } catch (error) {
      console.error('Failed to load business settings:', error);
      // Keep default tax rate if loading fails
    }
  };

  const handleProductSelect = (productId) => {
    const product = products.find(p => p.id === productId);
    setSelectedProduct(product);
    
    // Initialize product-specific fields
    if (product) {
      const fields = {
        base_options: {},  // e.g., {size: "Small", color: "Red"}
        custom_options: {},  // e.g., {engraving: "John Doe"}
        selected_addons: [],  // Array of addon IDs
        uploaded_files: []  // Array of file data
      };
      
      // Initialize base options (like size dropdowns)
      if (product.base_options) {
        Object.keys(product.base_options).forEach(optionKey => {
          const options = product.base_options[optionKey];
          if (options && options.length > 0) {
            fields.base_options[optionKey] = options[0].name;  // Default to first option
          }
        });
      }
      
      // Initialize custom text options
      if (product.custom_options) {
        Object.keys(product.custom_options).forEach(optionKey => {
          fields.custom_options[optionKey] = '';
        });
      }
      
      setProductFields(fields);
    }
  };

  const handleCustomerSearch = (searchTerm) => {
    if (searchTerm.length < 3) return;
    
    const found = customers.find(c => 
      c.email.toLowerCase() === searchTerm.toLowerCase() ||
      `${c.first_name} ${c.last_name}`.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    if (found) {
      setCustomerInfo({
        first_name: found.first_name,
        last_name: found.last_name,
        email: found.email,
        phone: found.phone || ''
      });
    }
  };

  const calculateBasePrice = () => {
    if (!selectedProduct) return 0;
    
    let basePrice = selectedProduct.price;
    
    // Add base options price increases (size, color, etc.)
    if (productFields.base_options && selectedProduct.base_options && typeof selectedProduct.base_options === 'object') {
      Object.entries(productFields.base_options).forEach(([optionKey, selectedValue]) => {
        const optionGroup = selectedProduct.base_options[optionKey];
        if (Array.isArray(optionGroup)) {
          const selectedOption = optionGroup.find(opt => opt && opt.name === selectedValue);
          if (selectedOption && selectedOption.price > 0) {
            basePrice += selectedOption.price;
          }
        }
      });
    }
    
    // Add addons price
    if (productFields.selected_addons && Array.isArray(productFields.selected_addons) && selectedProduct.addons && Array.isArray(selectedProduct.addons)) {
      productFields.selected_addons.forEach(addonName => {
        const addon = selectedProduct.addons.find(a => a.name === addonName);
        if (addon && addon.price > 0) {
          basePrice += addon.price;
        }
      });
    }
    
    // Add custom options price (if any have pricing)
    if (productFields.custom_options && selectedProduct.custom_options && typeof selectedProduct.custom_options === 'object') {
      Object.entries(selectedProduct.custom_options).forEach(([optionKey, customOption]) => {
        if (customOption.price && customOption.price > 0) {
          // Check if this custom option is filled out
          const customValue = productFields.custom_options[optionKey];
          if (customValue && customValue.trim() !== '') {
            basePrice += customOption.price;
          }
        }
      });
    }
    
    return basePrice;
  };

  const calculateSubtotal = () => {
    return calculateBasePrice() * orderDetails.quantity;
  };

  const calculateTax = () => {
    if (!selectedProduct || !orderDetails.include_tax) return 0;
    return calculateSubtotal() * taxRate;
  };

  const calculateShipping = () => {
    const shippingCosts = {
      'standard': 5.99,
      'express': 15.99,
      'overnight': 29.99,
      'pickup': 0
    };
    return shippingCosts[orderDetails.shipping_method] || 0;
  };

  const calculateTotal = () => {
    if (!selectedProduct) return 0;
    const subtotal = calculateSubtotal();
    const tax = calculateTax();
    const shipping = calculateShipping();
    return subtotal + tax + shipping;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('=== CREATE ORDER FORM SUBMITTED ===');
    console.log('Customer Info:', customerInfo);
    console.log('Selected Product:', selectedProduct);
    console.log('Order Details:', orderDetails);
    
    if (!customerInfo.first_name || !customerInfo.last_name || !customerInfo.email) {
      alert('Customer name and email are mandatory');
      console.log('Validation failed: Missing customer info');
      return;
    }
    
    if (!selectedProduct) {
      alert('Please select a product');
      console.log('Validation failed: No product selected');
      return;
    }
    
    console.log('Validation passed, creating order...');
    setLoading(true);
    
    try {
      const token = localStorage.getItem('access_token');
      
      const subtotal = calculateSubtotal();
      const tax = calculateTax();
      const shipping = calculateShipping();
      const total = calculateTotal();
      const depositAmount = orderDetails.deposit_amount || 0;
      const balanceOwed = total - depositAmount;
      
      // Build structured items array with pricing details for email template
      const itemOptions = [];
      const itemAddons = [];
      let personalizationPrice = 0;
      
      // Extract base options with prices
      if (productFields.base_options && selectedProduct.base_options) {
        Object.entries(productFields.base_options).forEach(([optionKey, selectedValue]) => {
          const optionGroup = selectedProduct.base_options[optionKey];
          if (Array.isArray(optionGroup)) {
            const selectedOption = optionGroup.find(opt => opt && opt.name === selectedValue);
            if (selectedOption) {
              itemOptions.push({
                name: `${optionKey}`,
                value: selectedValue,
                price: selectedOption.price || 0
              });
            }
          }
        });
      }
      
      // Extract addons with prices
      if (productFields.selected_addons && Array.isArray(productFields.selected_addons) && selectedProduct.addons) {
        productFields.selected_addons.forEach(addonName => {
          const addon = selectedProduct.addons.find(a => a.name === addonName);
          if (addon) {
            itemAddons.push({
              name: addon.name,
              price: addon.price || 0
            });
          }
        });
      }
      
      // Check for personalization pricing
      if (selectedProduct.custom_options && typeof selectedProduct.custom_options === 'object') {
        Object.entries(selectedProduct.custom_options).forEach(([optionKey, customOption]) => {
          if (customOption.price && customOption.price > 0) {
            const customValue = productFields.custom_options?.[optionKey];
            if (customValue && customValue.trim() !== '') {
              personalizationPrice += customOption.price;
            }
          }
        });
      }
      
      // Upload files if any
      let uploadedFilesData = [];
      if (productFields.uploaded_files && productFields.uploaded_files.length > 0) {
        console.log('Uploading files...', productFields.uploaded_files.length);
        for (const file of productFields.uploaded_files) {
          try {
            const formData = new FormData();
            formData.append('file', file);
            
            const uploadResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/upload`, {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${token}`
              },
              body: formData
            });
            
            if (uploadResponse.ok) {
              const uploadResult = await uploadResponse.json();
              uploadedFilesData.push({
                id: uploadResult.id,
                filename: uploadResult.filename,
                original_filename: file.name,
                file_url: uploadResult.file_url,
                file_size: uploadResult.size,
                content_type: uploadResult.content_type,
                thumbnail_url: uploadResult.thumbnail_url
              });
              console.log('File uploaded successfully:', file.name);
            } else {
              console.error('File upload failed:', file.name);
              alert(`Failed to upload file: ${file.name}`);
            }
          } catch (fileError) {
            console.error('Error uploading file:', file.name, fileError);
            alert(`Error uploading file: ${file.name}`);
          }
        }
        console.log('All files uploaded:', uploadedFilesData.length);
      }
      
      // Build custom_text_inputs from custom_options
      const customTextInputs = [];
      if (selectedProduct.custom_options && productFields.custom_options) {
        Object.entries(productFields.custom_options).forEach(([key, value]) => {
          if (value && value.trim() !== '') {
            const optionConfig = selectedProduct.custom_options[key];
            customTextInputs.push({
              name: optionConfig?.label || key,
              value: value,
              price: optionConfig?.price || 0
            });
          }
        });
      }
      
      const orderData = {
        customer_info: customerInfo,
        items: [{
          product_id: selectedProduct.id,
          product_name: selectedProduct.name,
          quantity: orderDetails.quantity,
          price: selectedProduct.price,
          selected_options: itemOptions,
          selected_addons: itemAddons,
          custom_text_inputs: customTextInputs,
          customization_notes: orderDetails.customization_notes,
          personalization_price: personalizationPrice,
          uploaded_files: uploadedFilesData
        }],
        shipping_address: orderDetails.shipping_address,
        payment_method: orderDetails.payment_method,
        shipping_method: orderDetails.shipping_method,
        subtotal: subtotal,
        tax: tax,
        include_tax: orderDetails.include_tax,
        shipping_cost: shipping,
        total: total,
        deposit_amount: depositAmount,
        balance_owed: balanceOwed,
        payment_status: balanceOwed <= 0 ? 'paid' : 'partially_paid'
      };
      
      console.log('Sending order data:', orderData);
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/create-customer-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(orderData)
      });
      
      console.log('API Response Status:', response.status);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('API Error:', errorData);
        throw new Error(errorData.detail || 'Failed to create order');
      }
      
      const result = await response.json();
      console.log('Order created successfully:', result);
      alert('Order created successfully! Customer will receive an email invitation.');
      
      // Reset form
      setSelectedProduct(null);
      setProductFields({});
      setCustomerInfo({ first_name: '', last_name: '', email: '', phone: '' });
      setOrderDetails({
        quantity: 1,
        customization_notes: '',
        shipping_address: { street: '', city: '', state: '', postal_code: '', country: 'US' },
        payment_method: 'bank_transfer',
        shipping_method: 'standard',
        deposit_amount: 0,
        include_tax: true
      });
      
    } catch (error) {
      console.error('=== ERROR CREATING ORDER ===');
      console.error('Error:', error);
      console.error('Stack:', error.stack);
      alert('Failed to create order: ' + error.message);
    } finally {
      console.log('Form submission complete, setting loading to false');
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Customer Information */}
      <div className={`${theme.card} p-6 rounded-lg shadow`}>
        <h3 className={`text-lg font-bold mb-4 ${theme.textPrimary}`}>👤 Customer Information</h3>
        
        <div className="mb-4">
          <label className={`block text-sm font-medium mb-2 ${theme.textPrimary}`}>
            Search Existing Customer
          </label>
          <input
            type="text"
            onChange={(e) => handleCustomerSearch(e.target.value)}
            placeholder="Type email or name to search..."
            className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.bgPrimary} ${theme.textPrimary}`}
          />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={`block text-sm font-medium mb-1 ${theme.textPrimary}`}>
              First Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              required
              value={customerInfo.first_name}
              onChange={(e) => setCustomerInfo({...customerInfo, first_name: e.target.value})}
              className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.bgPrimary} ${theme.textPrimary}`}
            />
          </div>
          <div>
            <label className={`block text-sm font-medium mb-1 ${theme.textPrimary}`}>
              Last Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              required
              value={customerInfo.last_name}
              onChange={(e) => setCustomerInfo({...customerInfo, last_name: e.target.value})}
              className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.bgPrimary} ${theme.textPrimary}`}
            />
          </div>
          <div>
            <label className={`block text-sm font-medium mb-1 ${theme.textPrimary}`}>
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              required
              value={customerInfo.email}
              onChange={(e) => setCustomerInfo({...customerInfo, email: e.target.value})}
              className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.bgPrimary} ${theme.textPrimary}`}
            />
          </div>
          <div>
            <label className={`block text-sm font-medium mb-1 ${theme.textPrimary}`}>Phone</label>
            <input
              type="tel"
              value={customerInfo.phone}
              onChange={(e) => setCustomerInfo({...customerInfo, phone: e.target.value})}
              className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.bgPrimary} ${theme.textPrimary}`}
            />
          </div>
        </div>
      </div>

      {/* Product Selection */}
      <div className={`${theme.card} p-6 rounded-lg shadow`}>
        <h3 className={`text-lg font-bold mb-4 ${theme.textPrimary}`}>🛍️ Product Selection</h3>
        <div>
          <label className={`block text-sm font-medium mb-2 ${theme.textPrimary}`}>
            Select Product <span className="text-red-500">*</span>
          </label>
          <select
            required
            value={selectedProduct?.id || ''}
            onChange={(e) => handleProductSelect(e.target.value)}
            className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.bgPrimary} ${theme.textPrimary}`}
          >
            <option value="">Choose a product...</option>
            {products.map(product => (
              <option key={product.id} value={product.id}>
                {product.name} - ${product.price}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Product-Specific Fields (Dynamic) */}
      {selectedProduct && (
        <>
          {/* Product Info & Quantity */}
          <div className={`${theme.card} p-6 rounded-lg shadow`}>
            <h3 className={`text-lg font-bold mb-4 ${theme.textPrimary}`}>📦 Order Details</h3>
            
            {/* Product Description */}
            {selectedProduct.description && (
              <div className={`mb-4 p-3 rounded ${theme.bgSecondary}`}>
                <p className={`text-sm ${theme.textSecondary}`}>{selectedProduct.description}</p>
              </div>
            )}
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${theme.textPrimary}`}>Quantity</label>
                <input
                  type="number"
                  min="1"
                  value={orderDetails.quantity}
                  onChange={(e) => setOrderDetails({...orderDetails, quantity: parseInt(e.target.value)})}
                  className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.bgPrimary} ${theme.textPrimary}`}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-2 ${theme.textPrimary}`}>Price per Unit</label>
                <input
                  type="text"
                  value={`$${selectedProduct.price}`}
                  disabled
                  className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.bgSecondary} ${theme.textSecondary}`}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-2 ${theme.textPrimary}`}>
                  {selectedProduct.in_stock ? '✅ In Stock' : '⚠️ Made to Order'}
                </label>
                <input
                  type="text"
                  value={selectedProduct.in_stock ? 'Available' : 'Custom Order'}
                  disabled
                  className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.bgSecondary} ${theme.textSecondary}`}
                />
              </div>
            </div>
          </div>

          {/* Base Options (Size, Color, etc.) */}
          {selectedProduct.base_options && Object.keys(selectedProduct.base_options).length > 0 && (
            <div className={`${theme.card} p-6 rounded-lg shadow`}>
              <h3 className={`text-lg font-bold mb-4 ${theme.textPrimary}`}>⚙️ Product Options</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(selectedProduct.base_options).map(([optionKey, options]) => (
                  <div key={optionKey}>
                    <label className={`block text-sm font-medium mb-2 ${theme.textPrimary} capitalize`}>
                      {optionKey} <span className="text-red-500">*</span>
                    </label>
                    <select
                      required
                      value={productFields.base_options?.[optionKey] || ''}
                      onChange={(e) => setProductFields({
                        ...productFields,
                        base_options: {...productFields.base_options, [optionKey]: e.target.value}
                      })}
                      className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.bgPrimary} ${theme.textPrimary}`}
                    >
                      {options.map((option, idx) => (
                        <option key={idx} value={option.name}>
                          {option.name} {option.price > 0 && `(+$${option.price.toFixed(2)})`}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Custom Text Options (Engraving, Personalization, etc.) */}
          {selectedProduct.custom_options && Object.keys(selectedProduct.custom_options).length > 0 && (
            <div className={`${theme.card} p-6 rounded-lg shadow`}>
              <h3 className={`text-lg font-bold mb-4 ${theme.textPrimary}`}>✏️ Personalization</h3>
              <div className="grid grid-cols-1 gap-4">
                {Object.entries(selectedProduct.custom_options).map(([optionKey, option]) => (
                  <div key={optionKey}>
                    <label className={`block text-sm font-medium mb-2 ${theme.textPrimary} capitalize`}>
                      {option.label || optionKey} 
                      {option.required && <span className="text-red-500"> *</span>}
                      {option.price > 0 && <span className="text-green-600"> (+${option.price.toFixed(2)})</span>}
                    </label>
                    {option.max_length && (
                      <p className={`text-xs mb-1 ${theme.textSecondary}`}>
                        Max {option.max_length} characters
                      </p>
                    )}
                    <input
                      type="text"
                      required={option.required}
                      maxLength={option.max_length || undefined}
                      value={productFields.custom_options?.[optionKey] || ''}
                      onChange={(e) => setProductFields({
                        ...productFields,
                        custom_options: {...productFields.custom_options, [optionKey]: e.target.value}
                      })}
                      placeholder={option.placeholder || `Enter ${option.label || optionKey}...`}
                      className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.bgPrimary} ${theme.textPrimary}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Addons */}
          {selectedProduct.addons && selectedProduct.addons.length > 0 && (
            <div className={`${theme.card} p-6 rounded-lg shadow`}>
              <h3 className={`text-lg font-bold mb-4 ${theme.textPrimary}`}>➕ Add-ons</h3>
              <div className="space-y-3">
                {selectedProduct.addons.map((addon, idx) => (
                  <div key={idx} className={`flex items-start gap-3 p-3 border ${theme.border} rounded-lg`}>
                    <input
                      type="checkbox"
                      id={`addon-${idx}`}
                      checked={productFields.selected_addons?.includes(addon.name) || false}
                      onChange={(e) => {
                        const addons = productFields.selected_addons || [];
                        if (e.target.checked) {
                          setProductFields({
                            ...productFields,
                            selected_addons: [...addons, addon.name]
                          });
                        } else {
                          setProductFields({
                            ...productFields,
                            selected_addons: addons.filter(a => a !== addon.name)
                          });
                        }
                      }}
                      className="mt-1 w-4 h-4 text-blue-600 rounded"
                    />
                    <label htmlFor={`addon-${idx}`} className="flex-1 cursor-pointer">
                      <div className={`font-medium ${theme.textPrimary}`}>
                        {addon.name} <span className="text-green-600">(+${addon.price.toFixed(2)})</span>
                      </div>
                      {addon.description && (
                        <p className={`text-sm ${theme.textSecondary}`}>{addon.description}</p>
                      )}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* File Upload */}
          {selectedProduct.file_upload_enabled && (
            <div className={`${theme.card} p-6 rounded-lg shadow`}>
              <h3 className={`text-lg font-bold mb-4 ${theme.textPrimary}`}>
                📎 File Upload {selectedProduct.file_upload_required && <span className="text-red-500">*</span>}
              </h3>
              <p className={`text-sm mb-3 ${theme.textSecondary}`}>
                Max {selectedProduct.max_files_allowed || 10} file(s), 
                Max size: {selectedProduct.max_file_size_mb || 10}MB each
              </p>
              <p className={`text-xs mb-3 ${theme.textSecondary}`}>
                Accepted: {selectedProduct.allowed_file_types || 'image/*,.pdf,.stl,.obj'}
              </p>
              
              <div className="space-y-3">
                <input
                  type="file"
                  multiple
                  accept={selectedProduct.allowed_file_types || 'image/*,.pdf,.stl,.obj'}
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    setProductFields({
                      ...productFields,
                      uploaded_files: files
                    });
                  }}
                  className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.bgPrimary} ${theme.textPrimary}`}
                />
                
                {productFields.uploaded_files && productFields.uploaded_files.length > 0 && (
                  <div className={`p-3 rounded ${theme.bgSecondary}`}>
                    <p className={`text-sm font-medium mb-2 ${theme.textPrimary}`}>
                      Files Selected: {productFields.uploaded_files.length}
                    </p>
                    <ul className={`text-xs ${theme.textSecondary} space-y-1`}>
                      {productFields.uploaded_files.map((file, idx) => (
                        <li key={idx}>
                          • {file.name} ({(file.size / 1024 / 1024).toFixed(2)}MB)
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                <p className={`text-xs ${theme.textSecondary} italic`}>
                  💡 Or customer can upload files after receiving the invitation email
                </p>
              </div>
            </div>
          )}

          {/* Shipping & Payment */}
          <div className={`${theme.card} p-6 rounded-lg shadow`}>
            <h3 className={`text-lg font-bold mb-4 ${theme.textPrimary}`}>📦 Shipping & Payment</h3>
            
            {/* Shipping Address - All Optional */}
            <div className="mb-4">
              <h4 className={`text-md font-semibold mb-2 ${theme.textPrimary}`}>
                Shipping Address <span className={`text-xs font-normal ${theme.textSecondary}`}>(Optional)</span>
              </h4>
              <p className={`text-xs mb-3 ${theme.textSecondary}`}>
                Leave blank if customer will provide address later or for pickup orders
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className={`block text-sm mb-1 ${theme.textPrimary}`}>Street</label>
                  <input
                    type="text"
                    value={orderDetails.shipping_address.street}
                    onChange={(e) => setOrderDetails({
                      ...orderDetails,
                      shipping_address: {...orderDetails.shipping_address, street: e.target.value}
                    })}
                    placeholder="Optional"
                    className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.bgPrimary} ${theme.textPrimary}`}
                  />
                </div>
                <div>
                  <label className={`block text-sm mb-1 ${theme.textPrimary}`}>City</label>
                  <input
                    type="text"
                    value={orderDetails.shipping_address.city}
                    onChange={(e) => setOrderDetails({
                      ...orderDetails,
                      shipping_address: {...orderDetails.shipping_address, city: e.target.value}
                    })}
                    placeholder="Optional"
                    className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.bgPrimary} ${theme.textPrimary}`}
                  />
                </div>
                <div>
                  <label className={`block text-sm mb-1 ${theme.textPrimary}`}>State</label>
                  <input
                    type="text"
                    value={orderDetails.shipping_address.state}
                    onChange={(e) => setOrderDetails({
                      ...orderDetails,
                      shipping_address: {...orderDetails.shipping_address, state: e.target.value}
                    })}
                    placeholder="Optional"
                    className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.bgPrimary} ${theme.textPrimary}`}
                  />
                </div>
                <div>
                  <label className={`block text-sm mb-1 ${theme.textPrimary}`}>Postal Code</label>
                  <input
                    type="text"
                    value={orderDetails.shipping_address.postal_code}
                    onChange={(e) => setOrderDetails({
                      ...orderDetails,
                      shipping_address: {...orderDetails.shipping_address, postal_code: e.target.value}
                    })}
                    placeholder="Optional"
                    className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.bgPrimary} ${theme.textPrimary}`}
                  />
                </div>
                <div>
                  <label className={`block text-sm mb-1 ${theme.textPrimary}`}>Country</label>
                  <input
                    type="text"
                    value={orderDetails.shipping_address.country}
                    onChange={(e) => setOrderDetails({
                      ...orderDetails,
                      shipping_address: {...orderDetails.shipping_address, country: e.target.value}
                    })}
                    placeholder="Optional"
                    className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.bgPrimary} ${theme.textPrimary}`}
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={`block text-sm mb-2 ${theme.textPrimary}`}>
                  Payment Method <span className="text-red-500">*</span>
                </label>
                <select
                  required
                  value={orderDetails.payment_method}
                  onChange={(e) => setOrderDetails({...orderDetails, payment_method: e.target.value})}
                  className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.bgPrimary} ${theme.textPrimary}`}
                >
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="cash">Cash</option>
                  <option value="check">Check</option>
                  <option value="credit_card">Credit Card</option>
                  <option value="paypal">PayPal</option>
                </select>
              </div>
              <div>
                <label className={`block text-sm mb-2 ${theme.textPrimary}`}>
                  Shipping Method <span className="text-red-500">*</span>
                </label>
                <select
                  required
                  value={orderDetails.shipping_method}
                  onChange={(e) => setOrderDetails({...orderDetails, shipping_method: e.target.value})}
                  className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.bgPrimary} ${theme.textPrimary}`}
                >
                  <option value="standard">Standard ($5.99)</option>
                  <option value="express">Express ($15.99)</option>
                  <option value="overnight">Overnight ($29.99)</option>
                  <option value="pickup">Local Pickup (Free)</option>
                </select>
              </div>
            </div>
          </div>

          {/* Payment & Deposit */}
          <div className={`${theme.card} p-6 rounded-lg shadow`}>
            <h3 className={`text-lg font-bold mb-4 ${theme.textPrimary}`}>💳 Deposit & Payment</h3>
            
            <div className="mb-4">
              <label className={`block text-sm font-medium mb-2 ${theme.textPrimary}`}>
                Deposit Amount (Optional)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                max={calculateTotal()}
                value={orderDetails.deposit_amount || 0}
                onChange={(e) => setOrderDetails({...orderDetails, deposit_amount: parseFloat(e.target.value) || 0})}
                placeholder="0.00"
                className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.bgPrimary} ${theme.textPrimary}`}
              />
              <p className={`text-xs mt-1 ${theme.textSecondary}`}>
                Enter the deposit amount the customer has already paid
              </p>
            </div>

            <div className={`p-4 rounded-lg ${theme.isDark ? 'bg-blue-900/20' : 'bg-blue-50'} border ${theme.isDark ? 'border-blue-700' : 'border-blue-200'}`}>
              <div className="flex justify-between mb-2">
                <span className={theme.textSecondary}>Total Amount:</span>
                <span className={`font-bold ${theme.textPrimary}`}>${calculateTotal().toFixed(2)}</span>
              </div>
              {orderDetails.deposit_amount > 0 && (
                <>
                  <div className="flex justify-between mb-2">
                    <span className={theme.textSecondary}>Deposit Paid:</span>
                    <span className="font-bold text-green-600">-${(orderDetails.deposit_amount || 0).toFixed(2)}</span>
                  </div>
                  <div className={`flex justify-between pt-2 border-t ${theme.isDark ? 'border-blue-700' : 'border-blue-200'}`}>
                    <span className={`font-bold ${theme.textPrimary}`}>Balance Owed:</span>
                    <span className="font-bold text-orange-600">${(calculateTotal() - (orderDetails.deposit_amount || 0)).toFixed(2)}</span>
                  </div>
                </>
              )}
            </div>

            <div className={`mt-4 p-3 rounded ${theme.isDark ? 'bg-amber-900/20' : 'bg-amber-50'} border ${theme.isDark ? 'border-amber-700' : 'border-amber-200'}`}>
              <p className={`text-sm ${theme.isDark ? 'text-amber-400' : 'text-amber-700'}`}>
                💡 Customer will be able to pay the remaining balance via PayPal or upon pickup/delivery
              </p>
            </div>
          </div>

          {/* Order Summary */}
          <div className={`${theme.card} p-6 rounded-lg shadow border-l-4 border-green-500`}>
            <h3 className={`text-lg font-bold mb-4 ${theme.textPrimary}`}>💰 Order Summary</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className={theme.textSecondary}>Product:</span>
                <span className={theme.textPrimary}>{selectedProduct.name}</span>
              </div>
              <div className="flex justify-between">
                <span className={theme.textSecondary}>Base Price:</span>
                <span className={theme.textPrimary}>${selectedProduct.price.toFixed(2)}</span>
              </div>
              
              {/* Show option price increases */}
              {productFields.base_options && Object.entries(productFields.base_options).map(([optionKey, selectedValue]) => {
                const optionGroup = selectedProduct.base_options?.[optionKey];
                const selectedOption = optionGroup?.find(opt => opt.name === selectedValue);
                if (selectedOption && selectedOption.price > 0) {
                  return (
                    <div key={optionKey} className="flex justify-between text-sm">
                      <span className={`${theme.textSecondary} pl-4`}>+ {optionKey}: {selectedValue}</span>
                      <span className="text-green-600">+${selectedOption.price.toFixed(2)}</span>
                    </div>
                  );
                }
                return null;
              })}
              
              {/* Show addon costs */}
              {productFields.selected_addons && Array.isArray(productFields.selected_addons) && productFields.selected_addons.map(addonName => {
                const addon = selectedProduct.addons?.find(a => a.name === addonName);
                if (addon && addon.price > 0) {
                  return (
                    <div key={addonName} className="flex justify-between text-sm">
                      <span className={`${theme.textSecondary} pl-4`}>+ {addonName}</span>
                      <span className="text-green-600">+${addon.price.toFixed(2)}</span>
                    </div>
                  );
                }
                return null;
              })}
              
              {/* Show custom option costs */}
              {selectedProduct.custom_options && typeof selectedProduct.custom_options === 'object' && Object.entries(selectedProduct.custom_options).map(([optionKey, customOption]) => {
                const customValue = productFields.custom_options?.[optionKey];
                if (customOption.price > 0 && customValue && customValue.trim() !== '') {
                  return (
                    <div key={optionKey} className="flex justify-between text-sm">
                      <span className={`${theme.textSecondary} pl-4`}>+ {optionKey}</span>
                      <span className="text-green-600">+${customOption.price.toFixed(2)}</span>
                    </div>
                  );
                }
                return null;
              })}
              
              <div className={`flex justify-between pt-2 border-t ${theme.border}`}>
                <span className={theme.textSecondary}>Unit Price (with options):</span>
                <span className={theme.textPrimary}>${calculateBasePrice().toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className={theme.textSecondary}>Quantity:</span>
                <span className={theme.textPrimary}>{orderDetails.quantity}</span>
              </div>
              <div className="flex justify-between">
                <span className={theme.textSecondary}>Subtotal:</span>
                <span className={theme.textPrimary}>${calculateSubtotal().toFixed(2)}</span>
              </div>
              
              {/* Tax Toggle Checkbox */}
              <div className={`flex justify-between items-center py-2 border-y ${theme.border}`}>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="includeTax"
                    checked={orderDetails.include_tax}
                    onChange={(e) => setOrderDetails({...orderDetails, include_tax: e.target.checked})}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="includeTax" className={`text-sm cursor-pointer ${theme.textSecondary}`}>
                    Include Tax ({(taxRate * 100).toFixed(1)}%)
                  </label>
                </div>
                <span className={theme.textPrimary}>
                  {orderDetails.include_tax ? `$${calculateTax().toFixed(2)}` : '$0.00'}
                </span>
              </div>
              {calculateShipping() > 0 && (
                <div className="flex justify-between">
                  <span className={theme.textSecondary}>Shipping ({orderDetails.shipping_method}):</span>
                  <span className={theme.textPrimary}>${calculateShipping().toFixed(2)}</span>
                </div>
              )}
              <div className={`flex justify-between text-lg font-bold pt-2 border-t ${theme.border}`}>
                <span className={theme.textPrimary}>Total:</span>
                <span className="text-xl font-bold text-green-600">${calculateTotal().toFixed(2)}</span>
              </div>
              {orderDetails.deposit_amount > 0 && (
                <div className="flex justify-between pt-1">
                  <span className={theme.textSecondary}>Balance After Deposit:</span>
                  <span className="text-xl font-bold text-orange-600">${(calculateTotal() - (orderDetails.deposit_amount || 0)).toFixed(2)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={loading}
              className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-semibold text-lg"
            >
              {loading ? 'Creating Order...' : '📧 Create Order & Send Invitation'}
            </button>
          </div>
        </>
      )}
    </form>
  );
};

// Orders & Fulfillment Section
export const OrdersFulfillmentSection = () => {
  const theme = useThemeClasses();
  const [activeSubTab, setActiveSubTab] = useState('orders');

  return (
    <div className="space-y-6">
      <div className={`border-b ${theme.border}`}>
        <nav className="flex space-x-8">
          {[
            { id: 'orders', label: '🛒 Orders', icon: '' },
            { id: 'created-orders', label: '📋 Created Orders', icon: '' },
            { id: 'ai-orders', label: '🤖 AI Orders', icon: '' },
            { id: 'designer-requests', label: '✏️ Designer Requests', icon: '' },
            { id: 'create-order', label: '📝 Create Order', icon: '' },
            { id: 'order-numbers', label: '🔢 Order Numbers', icon: '' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveSubTab(tab.id)}
              className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeSubTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : `border-transparent ${theme.textSecondary} hover:text-blue-600`
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {activeSubTab === 'orders' && <EnhancedOrdersManagement />}
      {activeSubTab === 'created-orders' && <CreatedOrdersManagement />}
      {activeSubTab === 'ai-orders' && <AIProductsOrders />}
      {activeSubTab === 'designer-requests' && <DesignerRequestsManager />}
      {activeSubTab === 'create-order' && <CreateOrderTab />}
      {activeSubTab === 'order-numbers' && <OrderNumberManagement />}
    </div>
  );
};

// Marketing & Comms Section
export const MarketingCommsSection = () => {
  const theme = useThemeClasses();
  const [activeSubTab, setActiveSubTab] = useState('promotions');

  return (
    <div className="space-y-6">
      <div className={`border-b ${theme.border}`}>
        <nav className="flex space-x-8">
          {[
            { id: 'promotions', label: '🎉 Promotions', icon: '' },
            { id: 'discount-codes', label: '🎫 Discount Codes', icon: '' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveSubTab(tab.id)}
              className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeSubTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : `border-transparent ${theme.textSecondary} hover:text-blue-600`
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {activeSubTab === 'promotions' && <MarketingManagement />}
      {activeSubTab === 'discount-codes' && <DiscountCodesManagement />}
    </div>
  );
};

// Website Section
export const WebsiteSection = () => {
  const theme = useThemeClasses();
  const [activeSubTab, setActiveSubTab] = useState('design');

  return (
    <div className="space-y-6">
      <div className={`border-b ${theme.border}`}>
        <nav className="flex space-x-8">
          {[
            { id: 'design', label: '🎨 Design', icon: '' },
            { id: 'theme', label: '🌟 Homepage Theme', icon: '' },
            { id: 'content', label: '📄 Content Pages', icon: '' },
            { id: 'legal', label: '⚖️ Legal Pages', icon: '' },
            { id: 'seo', label: '🔍 SEO', icon: '' },
            { id: 'email-templates', label: '✉️ Email Templates', icon: '' },
            { id: 'contact-form', label: '📧 Contact Form', icon: '' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveSubTab(tab.id)}
              className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeSubTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : `border-transparent ${theme.textSecondary} hover:text-blue-600`
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {activeSubTab === 'design' && <WebsiteCustomizationManagement />}
      {activeSubTab === 'theme' && <HomepageThemeSettings />}
      {activeSubTab === 'content' && <ContentManagement />}
      {activeSubTab === 'legal' && <PolicyManagement />}
      {activeSubTab === 'seo' && <SEOManagement />}
      {activeSubTab === 'email-templates' && <EmailTemplatesManagement />}
      {activeSubTab === 'contact-form' && <ContactFormManagement />}
    </div>
  );
};

// Customers Section
export const CustomersSection = () => {
  return <CustomersManagement />;
};

// Dashboard Section
export const DashboardSection = () => {
  const theme = useThemeClasses();
  const [activeSubTab, setActiveSubTab] = useState('analytics');

  return (
    <div className="space-y-6">
      <div className={`border-b ${theme.border}`}>
        <nav className="flex space-x-8">
          {[
            { id: 'analytics', label: '📊 Analytics', icon: '' },
            { id: 'financial', label: '💰 Financial Reports', icon: '' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveSubTab(tab.id)}
              className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeSubTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : `border-transparent ${theme.textSecondary} hover:text-blue-600`
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {activeSubTab === 'analytics' && <AnalyticsDashboard />}
      {activeSubTab === 'financial' && <FinancialReports />}
    </div>
  );
};

// Settings Section
export const SettingsSection = () => {
  const theme = useThemeClasses();
  const [activeSubTab, setActiveSubTab] = useState('shipping');

  return (
    <div className="space-y-6">
      <div className={`border-b ${theme.border}`}>
        <nav className="flex space-x-8">
          {[
            { id: 'shipping', label: '📦 Shipping', icon: '' },
            { id: 'business-info', label: '🏢 Business Info', icon: '' },
            { id: 'admin-users', label: '👥 Admin Users', icon: '' },
            { id: 'system', label: '⚙️ System', icon: '' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveSubTab(tab.id)}
              className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeSubTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : `border-transparent ${theme.textSecondary} hover:text-blue-600`
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {activeSubTab === 'shipping' && <ShippingSettings />}
      {activeSubTab === 'business-info' && <BusinessInfoSettings />}
      {activeSubTab === 'admin-users' && <AdminUserManagement />}
      {activeSubTab === 'system' && <SystemSettings />}
    </div>
  );
};